/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { EMPTY_DISPLAY } from "./cells";
import {
  panelCountStyles,
  sectionCardStyles,
  sectionEmptyStyles,
  sectionFooterStyles,
  sectionFootnoteStyles,
  sectionHeaderStyles,
  sectionRowPrimaryStyles,
  sectionRowSecondaryStyles,
  sectionRowStyles,
  sectionRowValueStyles,
  sectionShowAllStyles,
} from "./styles";

const displayOrEmpty = (value) =>
  value === null || value === undefined || value === "" ? EMPTY_DISPLAY : value;

const joinMeta = (parts) => parts.filter(Boolean).join(" · ");

// USD/en-US matches Cells.money's ActiveSupport::NumberHelper.number_to_currency output
// (symbol, thousands separator, "-$" sign placement), so a footer total reads the same
// way as every row above it instead of drifting to its own "$-" convention.
const CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// `null` here means "no row in this section carried a value" — distinct from a section
// that legitimately nets to zero — so the footer can render "--" instead of a misleading
// "$0.00" for a section made entirely of missing cells.
const sumAmounts = (rows, key) => {
  let total = 0;
  let hasValue = false;

  rows.forEach((row) => {
    // `Number(null)` is 0, not NaN — checked explicitly so a missing cell (value: null)
    // isn't miscounted as a real zero and mistaken for "this section actually earned $0".
    const raw = row?.[key]?.value;
    if (raw === null || raw === undefined) return;

    const value = Number(raw);
    if (Number.isFinite(value)) {
      total += value;
      hasValue = true;
    }
  });

  return hasValue ? total : null;
};

const moneyTotal = (rows, key) => {
  const total = sumAmounts(rows, key);
  if (total === null) return EMPTY_DISPLAY;

  // Rounds away both float drift from summing cents and stray "-0" before formatting.
  const rounded = Math.round(total * 100) / 100 || 0;
  return CURRENCY_FORMATTER.format(rounded);
};

const vipRow = (row, index) => ({
  key: row.check_in_id ?? `vip-${index}`,
  primary: displayOrEmpty(row.guest_name),
  secondary: joinMeta([
    displayOrEmpty(row.card_name),
    row.variant_name || null,
    row.covers === null || row.covers === undefined ? null : `${row.covers} covers`,
  ]),
  value: displayOrEmpty(row.checked_in_display),
});

const compRow = (row, index) => ({
  key: `${row.discount_name ?? "comp"}-${index}`,
  primary: displayOrEmpty(row.discount_name),
  secondary: `Applied ${row.count ?? 0} ${row.count === 1 ? "time" : "times"}`,
  value: displayOrEmpty(row.amount?.display),
});

const labelledRow = (amountKey) => (row, index) => ({
  key: row.guid ?? `${amountKey}-${index}`,
  primary: displayOrEmpty(row.label),
  secondary: null,
  value: displayOrEmpty(row[amountKey]?.display),
});

/**
 * The four drill-in sections, in the order a manager reads them. `emptyMessage` matters
 * as much as the rows do — an empty VIP list is a normal day, not a failure, because the
 * list is sourced from OwnersTable check-ins rather than free text.
 */
const SECTION_CONFIG = [
  {
    key: "vips",
    title: "VIP & Notable Guests",
    emptyMessage: "No VIP guests checked in on this day.",
    toRow: vipRow,
  },
  {
    key: "comps",
    title: "Comps",
    emptyMessage: "No discounts were applied on this day.",
    toRow: compRow,
    footerLabel: "Total discounts",
    footerValue: (rows) => moneyTotal(rows, "amount"),
  },
  {
    key: "meal_periods",
    title: "Meal Periods",
    emptyMessage: "No sales by meal period on this day.",
    toRow: labelledRow("net_sales"),
    footerLabel: "Total net sales",
    footerValue: (rows) => moneyTotal(rows, "net_sales"),
  },
  {
    key: "sales_categories",
    title: "Sales Categories",
    emptyMessage: "No sales by category on this day.",
    toRow: labelledRow("item_sales"),
    footerLabel: "Total item sales",
    footerValue: (rows) => moneyTotal(rows, "item_sales"),
    footnote:
      "Item sales excludes refunds, fundraising service charges and deferred revenue, so it will not always equal net sales.",
  },
];

const SectionRows = ({ rows, toRow, testIdPrefix, sectionKey }) => (
  <React.Fragment>
    {rows.map((row, index) => {
      const { key, primary, secondary, value } = toRow(row, index);

      return (
        <Box key={key} sx={sectionRowStyles} data-testid={`${testIdPrefix}-${sectionKey}-row`}>
          <Box>
            <Box sx={sectionRowPrimaryStyles}>{primary}</Box>
            {secondary ? <Box sx={sectionRowSecondaryStyles}>{secondary}</Box> : null}
          </Box>
          <Box sx={sectionRowValueStyles}>{value}</Box>
        </Box>
      );
    })}
  </React.Fragment>
);

const INITIAL_ROW_LIMIT = 50;

const Section = ({ section, rows, testIdPrefix }) => {
  const [expanded, setExpanded] = useState(false);
  const isTruncated = rows.length > INITIAL_ROW_LIMIT;
  const visibleRows = expanded || !isTruncated ? rows : rows.slice(0, INITIAL_ROW_LIMIT);
  const hiddenCount = rows.length - visibleRows.length;
  const toggleExpanded = () => setExpanded((value) => !value);

  return (
    <Box sx={sectionCardStyles} data-testid={`${testIdPrefix}-section-${section.key}`}>
      <Box sx={sectionHeaderStyles}>
        <span>{section.title}</span>
        <Box
          component="span"
          sx={panelCountStyles}
          data-testid={`${testIdPrefix}-count-${section.key}`}
        >
          {rows.length}
        </Box>
      </Box>

      {rows.length === 0 ? (
        <Box sx={sectionEmptyStyles} data-testid={`${testIdPrefix}-empty-${section.key}`}>
          {section.emptyMessage}
        </Box>
      ) : (
        <React.Fragment>
          <SectionRows
            rows={visibleRows}
            toRow={section.toRow}
            testIdPrefix={testIdPrefix}
            sectionKey={section.key}
          />
          {isTruncated ? (
            <Box
              sx={sectionShowAllStyles}
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              data-testid={`${testIdPrefix}-show-all-${section.key}`}
              onClick={toggleExpanded}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleExpanded();
                }
              }}
            >
              {expanded ? "Show fewer" : `Show ${hiddenCount} more`}
            </Box>
          ) : null}
          {section.footerLabel ? (
            <Box sx={sectionFooterStyles} data-testid={`${testIdPrefix}-total-${section.key}`}>
              <span>{section.footerLabel}</span>
              <span>{section.footerValue(rows)}</span>
            </Box>
          ) : null}
          {section.footnote ? (
            <Box sx={sectionFootnoteStyles} data-testid={`${testIdPrefix}-footnote-${section.key}`}>
              {section.footnote}
            </Box>
          ) : null}
        </React.Fragment>
      )}
    </Box>
  );
};

/**
 * Renders all four sections for one grain. The section list is fixed rather than derived
 * from the payload's keys so a section the API omits still shows its empty state instead
 * of silently disappearing.
 */
export const DailyLogbookSections = ({ sections, testIdPrefix }) => {
  const rowsByKey = useMemo(
    () =>
      SECTION_CONFIG.reduce((accumulator, section) => {
        const rows = sections?.[section.key];
        accumulator[section.key] = Array.isArray(rows) ? rows : [];
        return accumulator;
      }, {}),
    [sections],
  );

  return (
    <Box data-testid={`${testIdPrefix}-sections`}>
      {SECTION_CONFIG.map((section) => (
        <Section
          key={section.key}
          section={section}
          rows={rowsByKey[section.key]}
          testIdPrefix={testIdPrefix}
        />
      ))}
    </Box>
  );
};
